import { useEffect, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import type { DecoratedSharedContentWithFile } from '@witmetrics/utils';
import {
  useFile,
  useFileCollectionFromFileId,
  useProjectFromFileCollectionId,
} from '@witmetrics/data-store';
import { copyToClipboard } from '@witmetrics/utils/clipboard';
import { isBlank } from '@witmetrics/utils/strings';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { getUnisonUrl } from '@/api/urls';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { useString } from '@/hooks/useString';
import Dialog, { DialogBody, DialogTitle } from '@/components/Dialog';
import Toggle from '@/components/Toggle';
import Button from '@/components/Button';
import TextField from '@/components/inputs/TextField';
import IconButton from '@/components/IconButton';
import { CopyIcon, HideIcon, QRCodeIcon, ShowIcon } from '@/icons';
import ShareLinkDialog from '@/dialogs/ShareLinkDialog';
import {
  classes,
  createSharedContent,
  fetchSharedContent,
  isSendEnabled,
  parseEmails,
  sendInvites,
  updateLockedPassword,
  updateSharedContent,
} from './utils';

export type ShareFileDialogProps = {
  fileId: number;
  onClose: () => void;
};

export default function ShareFileDialog({
  fileId,
  onClose,
}: ShareFileDialogProps) {
  const { onApiError } = useAppState();
  const file = useFile(fileId);
  const fileCollection = useFileCollectionFromFileId(fileId);
  const project = useProjectFromFileCollectionId(fileCollection?.id);
  const [previousEmails, setPreviousEmails] = useString();
  const [password, setPassword] = useString(project?.user?.email || '');
  const [emails, setEmails] = useString(project?.user?.email || '');
  const [message, setMessage] = useString();
  const [sharedContent, setSharedContent] =
    useState<DecoratedSharedContentWithFile | null>(null);
  const [isFetchingData, toggleFetchingData] = useToggle(true);
  const [isPasswordHidden, togglePasswordHidden] = useToggle(true);
  const [isSending, toggleSending] = useToggle();
  const [isSavingPassword, toggleSavingPassword] = useToggle();
  const [isShareLinkDialogOpen, toggleShareLinkDialog] = useToggle();

  const isActive = !!sharedContent && sharedContent.enabled;

  useEffect(() => {
    handleFetchSharing();
  }, [file]);

  const handleFetchSharing = async () => {
    try {
      toggleFetchingData(true);
      const data = await fetchSharedContent(fileId);
      setSharedContent(data);
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching shared content');
      throw err;
    }
  };

  const handleToggleSharing = async (enabled: boolean) => {
    if (sharedContent) {
      // Just need to update it
      await updateSharedContent(sharedContent.id, enabled);
      setSharedContent({ ...sharedContent, enabled });
    } else {
      // Need to create it, defaults to unlocked & enabled
      const newSharedContent = await createSharedContent(fileId);
      setSharedContent(newSharedContent as DecoratedSharedContentWithFile);
    }
  };

  const handleToggleLocked = async (isLocked: boolean) => {
    try {
      if (!sharedContent) return;
      await updateLockedPassword(sharedContent.id, isLocked);
      setSharedContent({ ...sharedContent, isLocked });
    } catch (err) {
      onApiError(err, 'Error updating content');
    }
  };

  const handleSavePassword = async () => {
    try {
      if (!sharedContent) return;
      toggleSavingPassword(true);
      await updateLockedPassword(sharedContent.id, true, password);
      showSuccessToaster('Password saved');
      toggleSavingPassword(false);
    } catch (err) {
      toggleSavingPassword(false);
      onApiError(err, 'Error saving password');
    }
  };

  const getShareLink = () => {
    return `${getUnisonUrl()}/shared/${sharedContent?.slug || ''}`;
  };

  const handleCopy = async () => {
    await copyToClipboard(getShareLink());
    showSuccessToaster(`Copied to clipboard`);
  };

  const handleSend = async () => {
    try {
      toggleSending(true);
      const parsedEmails = parseEmails(emails);
      if (!sharedContent) {
        toggleSending(false);
        return showErrorToaster('Enable sharing to send invites');
      }
      if (parsedEmails.length > 10) {
        toggleSending(false);
        return showErrorToaster('Maximum of 10 email addresses at one time');
      }
      await sendInvites(sharedContent.id, parsedEmails, message);
      setPreviousEmails(emails);
      showSuccessToaster(`Email(s) sent successfully`);
      toggleSending(false);
    } catch (err) {
      toggleSending(false);
      onApiError(err, 'Error sending email(s)');
    }
  };

  const renderToggleLabel = () => {
    return isActive ? (
      <>
        <p className={classes.message}>
          Sharing is currently enabled for this file.
        </p>
        <p>
          Turning off sharing will disable previously shared links until
          re-enabled.
        </p>
      </>
    ) : (
      <>
        <p className={classes.message}>
          Sharing is currently disabled for this file.
        </p>
        <p>To share with a colleague, sharing must be enabled.</p>
      </>
    );
  };

  const renderLockLabel = () => {
    return sharedContent?.isLocked ? (
      <>
        <p className={classes.message}>
          This file is currently password protected.
        </p>
        <p>
          Turning off password protection will allow anyone with the link to
          access this file while sharing is enabled.
        </p>
      </>
    ) : (
      <>
        <p className={classes.message}>
          Password protection is currently disabled.
        </p>
        <p>
          Password protection limits who can access this file while sharing is
          enabled.
        </p>
      </>
    );
  };

  return (
    <>
      {isShareLinkDialogOpen && (
        <ShareLinkDialog
          link={getShareLink()}
          onClose={() => toggleShareLinkDialog(false)}
        />
      )}
      <Dialog className={classes.dialog} onClose={onClose}>
        <DialogTitle title="Share" onClose={onClose} />
        <DialogBody className={classes.body}>
          <div className={classes.section}>
            <div className={classes.toggleWrapper}>
              <Toggle
                active={isActive}
                className={classes.toggle}
                onChange={handleToggleSharing}
              />
              <div>{renderToggleLabel()}</div>
            </div>
            {isActive && (
              <div className={classes.linkSection}>
                <TextField
                  className={classes.textField}
                  label="Share link"
                  value={getShareLink()}
                  onChange={() => null}
                />
                <div className={classes.links}>
                  <IconButton
                    title="View QR code"
                    onClick={() => toggleShareLinkDialog(true)}>
                    <QRCodeIcon />
                  </IconButton>
                  <IconButton title="Copy link" onClick={handleCopy}>
                    <CopyIcon />
                  </IconButton>
                </div>
              </div>
            )}
          </div>
          {isActive && (
            <>
              <div className={classes.section}>
                <div className={classes.toggleWrapper}>
                  <Toggle
                    active={sharedContent?.isLocked}
                    className={classes.toggle}
                    onChange={handleToggleLocked}
                  />
                  <div>{renderLockLabel()}</div>
                </div>
                {sharedContent?.isLocked && (
                  <div className={classes.linkSection}>
                    <TextField
                      className={classes.textField}
                      type={isPasswordHidden ? 'password' : 'text'}
                      label="Password"
                      value={password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              title={isPasswordHidden ? 'Show' : 'Hide'}
                              onClick={() => togglePasswordHidden()}>
                              {isPasswordHidden ? <ShowIcon /> : <HideIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onChange={setPassword}
                    />
                    <div className={classes.links}>
                      <Button
                        disabled={isSavingPassword || isBlank(password)}
                        onClick={handleSavePassword}>
                        {isSavingPassword ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className={classes.section}>
                <div className={classes.invitesMessage}>
                  <p className={classes.message}>Send invitation email(s)</p>
                  <p>
                    Enter up to 10 email addresses (comma-separated) to invite
                    others to access this file. They will receive an email with
                    a link and an optional message.
                  </p>
                </div>
                <div className={classes.emailsWrapper}>
                  <TextField
                    className={classes.textField}
                    label="Email(s)"
                    value={emails}
                    onChange={setEmails}
                  />
                  <Button
                    disabled={
                      isSending || !isSendEnabled(emails, previousEmails)
                    }
                    onClick={handleSend}>
                    {isSending ? 'Sending...' : 'Send'}
                  </Button>
                </div>
                <TextField
                  multiline
                  className={classes.textArea}
                  minRows={4}
                  maxRows={6}
                  label="Message (optional)"
                  value={message}
                  onChange={setMessage}
                />
              </div>
            </>
          )}
        </DialogBody>
      </Dialog>
    </>
  );
}
